import type { CSSProperties } from 'react';
import { Pattern } from '@/components/patterns';
import { CATEGORIES, type EventKind } from '@/constants/categories';
import { formatDayShort, formatTime, hasKnownTime } from '@/lib/dates';
import { upperCity } from '@/lib/city';
import { cn } from '@/lib/cn';

type PosterLayout = 'editorial' | 'discover-card';

interface Props {
  kind: EventKind;
  title: string;
  city?: string | null;
  startsAt: string;
  className?: string;
  ratio?: 'portrait' | 'landscape' | 'square' | 'banner';
  showLF?: boolean;
  minimal?: boolean;
  layout?: PosterLayout;
  seed?: string | number;
}

const ratios = {
  portrait: 'aspect-[3/4]',
  landscape: 'aspect-[16/9]',
  square: 'aspect-square',
  banner: 'aspect-[21/9]',
};

const DISCOVER_CARD_BASE_COLORS: Record<EventKind, string> = {
  market: '#D44A2A',
  family: '#4A6D4F',
  cultural: '#5B3A7F',
  music: '#142B4E',
  fair: '#8A5E2E',
  funfair: '#8A5E2E',
  circus: '#7A2E2E',
  food: '#D44A2A',
  sport: '#142B4E',
  motorsport: '#B02E3D',
  firework: '#B02E3D',
  other: '#1A1714',
};

export const DISCOVER_CARD_BADGES: Record<EventKind, string> = {
  market: 'MARCHÉ',
  family: 'FAMILLE',
  cultural: 'CULTURE',
  music: 'MUSIQUE',
  fair: 'FOIRE',
  funfair: 'FÊTE',
  circus: 'CIRQUE',
  food: 'GASTRO',
  sport: 'SPORT',
  motorsport: 'MOTEUR',
  firework: 'FEU',
  other: 'SORTIE',
};

export const POSTER_PAPER_HEX = '#F5EFE4';
export const POSTER_PAPER_DIM = 'rgb(245 239 228 / 0.6)';
export const POSTER_PAPER_SOFT = 'rgb(245 239 228 / 0.8)';
export const POSTER_CHAMPAGNE_HEX = '#C89B5A';
const DARK_MIX_HEX = '#14110E';

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function hashSeed(seed: string | number) {
  const value = String(seed);
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(31, hash) + value.charCodeAt(index);
  }

  return hash >>> 0;
}

function hexToRgb(hex: string) {
  const normalized = hex.replace('#', '');
  const parsed = Number.parseInt(normalized, 16);

  return [
    (parsed >> 16) & 255,
    (parsed >> 8) & 255,
    parsed & 255,
  ] as const;
}

function rgbToHex(red: number, green: number, blue: number) {
  return `#${[red, green, blue]
    .map((channel) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

function rgbToHsl(red: number, green: number, blue: number) {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) {
    return [0, 0, lightness * 100] as const;
  }

  const saturation = lightness > 0.5
    ? delta / (2 - max - min)
    : delta / (max + min);

  let hue = 0;

  switch (max) {
    case r:
      hue = (g - b) / delta + (g < b ? 6 : 0);
      break;
    case g:
      hue = (b - r) / delta + 2;
      break;
    default:
      hue = (r - g) / delta + 4;
      break;
  }

  return [hue * 60, saturation * 100, lightness * 100] as const;
}

function hueToRgb(p: number, q: number, t: number) {
  let next = t;

  if (next < 0) next += 1;
  if (next > 1) next -= 1;
  if (next < 1 / 6) return p + (q - p) * 6 * next;
  if (next < 1 / 2) return q;
  if (next < 2 / 3) return p + (q - p) * (2 / 3 - next) * 6;
  return p;
}

function hslToRgb(hue: number, saturation: number, lightness: number) {
  const h = hue / 360;
  const s = saturation / 100;
  const l = lightness / 100;

  if (s === 0) {
    const value = l * 255;
    return [value, value, value] as const;
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return [
    hueToRgb(p, q, h + 1 / 3) * 255,
    hueToRgb(p, q, h) * 255,
    hueToRgb(p, q, h - 1 / 3) * 255,
  ] as const;
}

function adjustHexLightness(hex: string, lightnessDelta: number) {
  const [red, green, blue] = hexToRgb(hex);
  const [hue, saturation, lightness] = rgbToHsl(red, green, blue);
  const [nextRed, nextGreen, nextBlue] = hslToRgb(hue, saturation, clamp(lightness + lightnessDelta, 0, 100));

  return rgbToHex(nextRed, nextGreen, nextBlue);
}

function mixHex(hex: string, otherHex: string, otherWeight: number) {
  const [red, green, blue] = hexToRgb(hex);
  const [otherRed, otherGreen, otherBlue] = hexToRgb(otherHex);
  const weight = clamp(otherWeight, 0, 1);

  return rgbToHex(
    red * (1 - weight) + otherRed * weight,
    green * (1 - weight) + otherGreen * weight,
    blue * (1 - weight) + otherBlue * weight,
  );
}

function discoverPosterStyle(kind: EventKind, seed: string | number) {
  const hash = hashSeed(seed);
  const baseColor = DISCOVER_CARD_BASE_COLORS[kind] ?? DISCOVER_CARD_BASE_COLORS.other;
  const lightnessDelta = ((Math.floor(hash / 3) % 5) - 2) * 4;
  const backgroundLight = adjustHexLightness(baseColor, lightnessDelta);
  const backgroundDark = mixHex(backgroundLight, DARK_MIX_HEX, 0.28);

  return {
    hash,
    style: {
      ['--fallback-poster-bg-light' as const]: backgroundLight,
      ['--fallback-poster-bg-dark' as const]: backgroundDark,
      color: POSTER_PAPER_HEX,
    } as CSSProperties,
  };
}

export function FallbackPoster({ kind, title, city, startsAt, className, ratio = 'landscape', showLF = true, minimal = false, layout = 'editorial', seed }: Props) {
  const meta = CATEGORIES[kind];
  const isDiscoverCard = layout === 'discover-card' && !minimal;
  const { hash, style } = discoverPosterStyle(kind, seed ?? title ?? startsAt);
  const titlePlacement = hash % 3;

  return (
    <div
      className={cn('relative w-full overflow-hidden rounded-md', ratios[ratio], isDiscoverCard && 'fallback-poster-bg', className)}
      style={isDiscoverCard ? style : { backgroundColor: `rgb(var(${meta.colorVar}))`, color: 'rgb(var(--paper-elev))' }}
    >
      <Pattern id={meta.pattern} opacity={0.085} />
      {isDiscoverCard && (
        <div className="absolute inset-0 flex flex-col p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <span className="t-mono-sm max-w-[42%] truncate uppercase tracking-[0.08em]" style={{ color: POSTER_PAPER_DIM }}>
              {DISCOVER_CARD_BADGES[kind]}
            </span>
            <span className="t-mono-sm min-w-0 max-w-[58%] text-right leading-[1.2] tracking-[0.04em]" style={{ color: POSTER_PAPER_DIM }}>
              {formatDayShort(startsAt).toUpperCase()}{hasKnownTime(startsAt) && ` · ${formatTime(startsAt)}`}
            </span>
          </div>

          <div
            className={cn(
              'flex flex-1',
              titlePlacement === 0 && 'items-start pt-5',
              titlePlacement === 1 && 'items-center',
              titlePlacement === 2 && 'items-end pb-4',
            )}
          >
            <h3 className="max-w-[84%] font-serif text-[clamp(20px,5.4vw,24px)] italic leading-[0.92] tracking-[-0.02em] line-clamp-3" style={{ color: POSTER_PAPER_HEX }}>
              {title}
            </h3>
          </div>

          <div className="pr-9">
            <span className="mb-3 block h-px w-10" style={{ backgroundColor: POSTER_CHAMPAGNE_HEX }} />
            <div className="font-mono text-[11px] font-medium uppercase leading-[14px] tracking-[0.08em]" style={{ color: POSTER_PAPER_SOFT }}>
              {upperCity(city) ?? '—'}
            </div>
          </div>
        </div>
      )}

      {!minimal && !isDiscoverCard && (
        <div className="absolute inset-0 p-5 flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="t-cap" style={{ color: 'rgb(var(--paper-elev) / 0.85)' }}>{meta.label}</span>
            <span className="t-mono-xs" style={{ color: 'rgb(var(--paper-elev) / 0.85)' }}>{formatDayShort(startsAt).toUpperCase()}{hasKnownTime(startsAt) && ` · ${formatTime(startsAt)}`}</span>
          </div>
          <div>
            <div className="font-serif italic leading-[0.95] text-[clamp(28px,7vw,46px)] tracking-tight max-w-[88%]">{meta.word}</div>
            <div className="t-cap mt-3" style={{ color: 'rgb(var(--paper-elev) / 0.8)' }}>
              <span className="filet-court inline-block align-middle mr-2" /> {upperCity(city) ?? '—'}
            </div>
          </div>
        </div>
      )}
      {minimal && (
        <div className="absolute inset-0 grid place-items-center">
          <div className="font-serif italic leading-none tracking-tight text-[clamp(40px,12vw,96px)]" style={{ color: 'rgb(var(--paper-elev) / 0.9)' }}>{meta.word}</div>
        </div>
      )}
      {showLF && !minimal && (
        <svg
          aria-hidden
          viewBox="0 0 16 16"
          width="16"
          height="16"
          className="absolute bottom-3 right-3"
          style={{ color: 'rgb(var(--paper-strong) / 0.4)' }}
        >
          {/* Hexagone évoquant le territoire métropolitain — signature silencieuse */}
          <polygon
            points="8,1 14.5,4.75 14.5,11.25 8,15 1.5,11.25 1.5,4.75"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}
